import React from 'react'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { OriginatingReportHistory } from '../report/OriginatingReportHistory'
import { OriginatingReportModalBottomSheet } from '../report/OriginatingReportModalBottomSheet'
import { ReportTile } from '../report/ReportTile'
import { usePromiseManagerOnLoad } from '../util/PromiseManager'
import { SVG_OfflineCloud } from '../svg/SVG_OfflineCloud'
import { ReportHistoryExpandingButton } from '../report/ReportHistoryExpandingButton'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import type { Nullable } from '@ui5/webcomponents-react'
import { fetchTranslatedHistoricalReport } from '../report/fetchTranslatedHistoricalReport'

interface OfflineExandingButtonProps {
    expectedReportCount: number
    isExpanded: boolean
    onRefresh: () => void
    rootClassName?: React.HTMLAttributes<HTMLDivElement>['className']
    toggleExpansion: () => void
}

function OfflineExandingButton(props: OfflineExandingButtonProps) {
    return (
        <ReportHistoryExpandingButton
            expectedReportCount={props.expectedReportCount}
            isExpanded={props.isExpanded}
            rootClassName={props.rootClassName}
            toggleExpansion={props.toggleExpansion}
        >
            <div className="AppCore__flow__ExecutionPageOriginatingReportHistory__OfflineExandingButton-content">
                <div>
                    <SVG_OfflineCloud />
                </div>
                <div className="AppCore__flow__ExecutionPageOriginatingReportHistory__OfflineExandingButton-msgPrimary">
                    {Translations.t('appcore:common.youAreCurrentlyOffline')}
                </div>
                <div className="AppCore__flow__ExecutionPageOriginatingReportHistory__OfflineExandingButton-msgSecondary">
                    {Translations.t('appcore:common.originatingReportGoOnline')}
                </div>
                <div>
                    <zaptic-button size="small" type="secondary" onClick={props.onRefresh}>
                        {Translations.t('appcore:common.refresh')}
                    </zaptic-button>
                </div>
            </div>
        </ReportHistoryExpandingButton>
    )
}

const ROOT_CLASS_NAME = 'AppCore__flow__ExecutionPageOriginatingReportHistory'

interface Props {
    deps: CommonDeps
    expectedReportCount: number
    featureFlags: Pick<CamelCaseFlags, 'reportsTranslation'>
    fetchReportHistory: () => Promise<HistoricalReport[]>
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    renderCachedImage: RenderCachedImage
    routeToPostPage: (postId: UUID) => void
    routeToReportPage: (reportId: number) => void
}

interface ReportState {
    isOnline: boolean
    reports: HistoricalReport[]
}

async function fetchReportState(deps: CommonDeps, fetchFn: () => Promise<HistoricalReport[]>): Promise<ReportState> {
    const isOnline = await deps.isOnline()
    const reports = isOnline ? await fetchFn() : []

    return { isOnline, reports }
}

export function ExecutionPageOriginatingReportHistory(props: Props) {
    if (props.expectedReportCount === 0) {
        return null
    }

    const [highlightedReport, setHighlightedReport] = React.useState<Nullable<HistoricalReport>>(null)
    const [isTranslationVisible, setIsTranslationVisible] = React.useState(false)
    const [isOfflineExpanded, setIsOfflineExpanded] = React.useState(false)
    const translatedReports = React.useRef<Map<number, HistoricalReport>>(new Map())

    const promiseManager = usePromiseManagerOnLoad<ReportState>(props.deps, () =>
        fetchReportState(props.deps, props.fetchReportHistory)
    )

    const fetchTranslation = async () =>
        fetchTranslatedHistoricalReport(props.deps, highlightedReport, translatedReports.current)

    const onTranslate = (report: Nullable<HistoricalReport>) => setIsTranslationVisible(!!report)

    return promiseManager.render((reportState) => {
        const translatedReport =
            (isTranslationVisible && translatedReports.current.get(highlightedReport?.report.id)) || null

        return reportState.isOnline ? (
            <>
                <OriginatingReportHistory
                    deps={props.deps}
                    expectedReportCount={props.expectedReportCount}
                    fetchReports={() => Promise.resolve(reportState.reports)}
                    renderReport={(report) => (
                        <ReportTile
                            commonDeps={props.deps}
                            onClick={() => {
                                setHighlightedReport(report)
                            }}
                            report={report}
                        />
                    )}
                    rootClassName={ROOT_CLASS_NAME}
                    expandImmediately={isOfflineExpanded}
                />

                <OriginatingReportModalBottomSheet
                    deps={props.deps}
                    featureFlags={props.featureFlags}
                    fetchTranslation={fetchTranslation}
                    onClose={() => {
                        setIsTranslationVisible(false)
                        setHighlightedReport(null)
                    }}
                    onOpenFile={props.onOpenFile}
                    onReportPostClick={props.routeToPostPage}
                    onTranslate={onTranslate}
                    renderCachedImage={props.renderCachedImage}
                    report={translatedReport || highlightedReport}
                    routeToReturnHere={() => {}}
                />
            </>
        ) : (
            <OfflineExandingButton
                expectedReportCount={props.expectedReportCount}
                isExpanded={isOfflineExpanded}
                onRefresh={() =>
                    promiseManager.trigger(() => {
                        if (!isOfflineExpanded) {
                            setIsOfflineExpanded(true)
                        }

                        return fetchReportState(props.deps, props.fetchReportHistory)
                    })
                }
                rootClassName={ROOT_CLASS_NAME}
                toggleExpansion={() => setIsOfflineExpanded(!isOfflineExpanded)}
            />
        )
    })
}
